import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSetAtom } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";

import { isAuthenticatedVar } from "@/variables/globalVar";
import {
  getAccountQueryKey,
  useLogin,
  useGetAuthSocialList,
} from "shared/apiHooks";
import { type SocialApp } from "shared/apiClient";
import { getText } from "shared/lib";

import { useCaptureEvent, CaptureEventType } from "@/hooks";

import { IconSpinner } from "@/components/icons";
import {
  Button,
  Input,
  Separator,
  SocialLoginButtons,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "shared/components";
import { TermsOfService } from "@/components/auth/TermsOfService";

export function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [_, setSearchParams] = useSearchParams();

  const { captureEvent } = useCaptureEvent();

  const setAuthenticatedVar = useSetAtom(isAuthenticatedVar);

  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [socialApps, setSocialApps] = useState<Array<SocialApp>>([]);

  const {
    data: authSocialListResponse,
    error: authSocialListError,
    isLoading: isLoadingAuthSocialList,
  } = useGetAuthSocialList();

  useEffect(() => {
    if (authSocialListError) {
      console.error(
        "<Login> useGetAuthSocialList() onError()",
        authSocialListError,
      );
      setErrorMsg(getText("Failed to load social login options."));
    }
  }, [authSocialListError]);

  useEffect(() => {
    if (authSocialListResponse) {
      const results = authSocialListResponse.data?.results || [];
      const transformedResults = results.map((app) => ({
        ...app,
        login_url: app.login_url ?? "", // Fallback to an empty string if login_url is undefined
      }));
      setSocialApps(transformedResults);
    }
  }, [authSocialListResponse]);

  const { isPending, mutate } = useLogin({
    onError: (error) => {
      console.error("<Login> useLogin() onError()", error);
      setAuthenticatedVar(false);
      const response = error.response;
      if (response?.data && Array.isArray(response.data["non_field_errors"])) {
        setErrorMsg(response.data["non_field_errors"][0]);
      } else if (response?.data && "detail" in response?.data) {
        setErrorMsg(response.data.detail as string);
      } else {
        setErrorMsg(
          getText(
            "There was an unexpected error logging you in. Please refresh your browser and try again, or contact support for assistance.",
          ),
        );
      }
    },
    onSuccess: (response) => {
      console.log("<Login> useLogin() onSuccess()", response);
      if (response.data) {
        setSearchParams((prev) => {
          prev.delete("mode");
          return prev;
        });

        setErrorMsg("");
        queryClient.setQueryData(getAccountQueryKey(), response);

        captureEvent(CaptureEventType.USER_LOGGED_IN, {
          email: response.data.email,
        });

        setAuthenticatedVar(true);
      }
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const email = emailRef.current?.value || "";
    const password = passwordRef.current?.value || "";

    setErrorMsg("");
    mutate({ body: { email, password } });
  };

  return (
    <Tabs defaultValue="login" className="w-full">
      <TabsList className="border-blueBorder grid w-full grid-cols-2 rounded-none border-b">
        <TabsTrigger
          value="login"
          className="rounded-none border-b-0 data-[state=active]:bg-transparent"
        >
          {getText("Log in")}
        </TabsTrigger>
        <button
          type="button"
          onClick={() => {
            navigate("/signup");
          }}
          className="text-bodymd inline-flex items-center justify-center rounded-none border-b-0 py-3 font-semibold whitespace-nowrap transition-all hover:text-black disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-transparent"
        >
          {getText("Sign up")}
        </button>
      </TabsList>

      <TabsContent value="login" className="flex flex-col gap-6 px-0 py-6">
        <SocialLoginButtons socialApps={socialApps} disabled={isPending} />

        <div className="flex items-center gap-2">
          <Separator className="w-auto grow" />
          <span className="text-bodyxs text-textSubdued">
            {getText("or log in with email")}
          </span>
          <Separator className="w-auto grow" />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label
                className="text-headingsm text-textPrimary"
                htmlFor="email"
              >
                {getText("Email address")}
              </label>
              <Input
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect="off"
                className="border-interactiveBorder text-bodymd rounded-lg p-4"
                disabled={isPending}
                id="email"
                ref={emailRef}
                required
                type="email"
              />

              <p className="text-bodyxs text-textSubdued">
                {getText(
                  "Your email address is used to send you a copy of your enquiry's result.",
                )}{" "}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <label
                className="text-headingsm text-textPrimary"
                htmlFor="password"
              >
                {getText("Password")}
              </label>
              <Input
                autoCapitalize="none"
                autoComplete="password"
                autoCorrect="off"
                className="border-interactiveBorder text-bodymd rounded-lg p-4"
                disabled={isPending}
                id="password"
                ref={passwordRef}
                required
                type="password"
              />
            </div>
          </div>

          <div className="grid gap-6">
            <div>
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  navigate("/forgotpassword");
                }}
                className="text-textLink hover:text-textLinkHover text-bodymd mt-1 underline"
              >
                {getText("Forgot your password?")}
              </button>
            </div>

            <TermsOfService />

            <Button className="!text-headingmd" disabled={isPending}>
              {getText("Log in")}
              {isPending && (
                <IconSpinner className="mr-2 h-4 w-4 animate-spin" />
              )}
            </Button>

            <div
              hidden={errorMsg === ""}
              className="border-destructiveBorder bg-destructiveBgDefault text-destructiveText mt-6 rounded-sm border p-3"
            >
              {errorMsg}
            </div>
          </div>
        </form>
      </TabsContent>
    </Tabs>
  );
}
